import {
    Box,
    TextField,
    TextFieldProps,
    useTheme,
    alpha,
    InputAdornment,
    IconButton,
    Tooltip,
    Autocomplete,
} from "@mui/material";
import { CheckCircle, AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../../configureStore";
import { ExtractTask } from "../../../../interface/returns";
import { Loan } from "../../../../interface/loan";
import { normalizeTins } from "../../../../utils";
import { addApprovedMatchValue, removeApprovedMatchValue, setFieldInvalid } from "../../../../redux/reducer/returns";
import { TextStyleProps } from "../types/formConfig";
import { FormMetaContext } from "./FormMetaContext";
import { buildInvalidKey, isMmDdYyyyInvalid } from "./collectDateValidationFields";

interface FormTextFieldProps extends Omit<TextFieldProps, "value" | "onChange" | "sx"> {
    fieldPath: string;
    value: any;
    handleFieldChange: (fieldPath: string, value: any) => void;
    isImportant: boolean;
    getFieldConfidence?: (fieldPath: string) => number;
    currentExtraction?: any;
    width?: number | string;
    labelStyle?: TextStyleProps;
    inputStyle?: TextStyleProps;
    indexLabel?: string;
    checkDateFormat?: boolean;
}

type MatchableFieldType = "tin" | "firstName" | "lastName" | "businessName" | "name" | null;

const normalizeString = (str: string): string => str?.toLowerCase().trim() || "";

/**
 * Reusable TextField component for form data entry
 * Automatically handles field path, change events, important field styling, and confidence indicators
 */
const FormTextField = ({
    fieldPath,
    value,
    handleFieldChange,
    isImportant,
    getFieldConfidence,
    currentExtraction,
    width,
    labelStyle,
    inputStyle,
    indexLabel,
    checkDateFormat,
    ...textFieldProps
}: FormTextFieldProps) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const importantBorderColor = theme.palette.mode === "dark" ? "#ffb74d" : "#f57c00";

    const approvedMatchValues = useSelector<RootState, Record<string, string[]>>(
        ({ returns }) => returns.approvedMatchValues,
    );

    const matchableFieldType = useMemo<MatchableFieldType>(() => {
        const splitField = fieldPath.split(".");
        const lowerCase = splitField[splitField.length - 1].toLowerCase();
        const lowerCaseLabel: string | undefined = (textFieldProps.label as string | undefined)?.toLowerCase();

        if (
            lowerCase.includes("tin") ||
            lowerCase.includes("ssn") ||
            lowerCase.includes("socialsecurity") ||
            lowerCase.includes("ein") ||
            lowerCase.includes("employeridentification") ||
            lowerCase.includes("taxpayeridentification") ||
            lowerCase.includes("identifyingnumber") ||
            (lowerCaseLabel && lowerCaseLabel.includes("social security number"))
        ) {
            return "tin";
        }
        if (lowerCase.includes("firstname") || lowerCase === "first") {
            return "firstName";
        }
        if (lowerCase.includes("lastname") || lowerCase === "last") {
            return "lastName";
        }
        if (lowerCase.includes("name") || (lowerCaseLabel && lowerCaseLabel.includes("name of officer"))) {
            return "name";
        }
        if (
            lowerCase.includes("businessname") ||
            lowerCase.includes("companyname") ||
            lowerCase.includes("entityname") ||
            lowerCase.includes("corporatename") ||
            lowerCase.includes("corporationname") ||
            lowerCase.includes("partnershipname")
        ) {
            return "businessName";
        }
        return null;
    }, [fieldPath]);

    const loan = useSelector<RootState, Loan>(({ returns }) => (returns.activeReturnTask as ExtractTask)?.blobs.loan);

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            handleFieldChange(fieldPath, event.target.value);
        },
        [fieldPath, handleFieldChange],
    );

    const confidence = getFieldConfidence ? getFieldConfidence(fieldPath) : 1;
    const lowConfidence = currentExtraction && confidence < 0.5;

    // First two digits of an MM/DD/YYYY value can't exceed 12 — anything higher means the user
    // typed DD/MM/YYYY (or similar wrong-format input). Empty values are not flagged.
    const isInvalidDate = useMemo(
        () => !!checkDateFormat && isMmDdYyyyInvalid(value),
        [checkDateFormat, value],
    );

    const hasError = lowConfidence || isInvalidDate;
    const errorHelperText = isInvalidDate
        ? "This date needs to be formatted as MM/DD/YYYY"
        : lowConfidence
          ? "Low confidence"
          : "";

    // Report this field's validity to Redux so the Confirm Extraction button can disable
    // and the error banner persists across page/form switches for the whole extract task.
    // The entry stays until the user navigates back to this field and fixes the value
    // (at which point this effect re-runs with isInvalidDate=false and clears it) or until
    // a task-level reset/save fires (handled by the reducer). Key is composite so the same
    // fieldPath on different forms/pages doesn't collide.
    const formMeta = useContext(FormMetaContext);
    useEffect(() => {
        if (!checkDateFormat) return;
        const invalidKey = buildInvalidKey(formMeta.formType, formMeta.pageNumber, fieldPath);
        dispatch(setFieldInvalid(invalidKey, isInvalidDate, formMeta));
    }, [dispatch, fieldPath, checkDateFormat, isInvalidDate, formMeta]);

    const normalizedValue = useMemo(() => {
        if (!matchableFieldType || !value) return "";
        return matchableFieldType === "tin" ? normalizeTins(String(value)) : normalizeString(String(value));
    }, [matchableFieldType, value]);

    const matchableOptions = useMemo<string[]>(() => {
        if (!matchableFieldType || !loan) return [];

        let loanValues: string[] = [];
        switch (matchableFieldType) {
            case "tin":
                loanValues = [...loan.borrowers, ...loan.businesses].map((b) => b.tin).filter(Boolean) as string[];
                break;
            case "firstName":
                loanValues = loan.borrowers.map((b) => b.firstName).filter(Boolean);
                break;
            case "lastName":
                loanValues = loan.borrowers.map((b) => b.lastName).filter(Boolean);
                break;
            case "name":
                loanValues = [
                    ...loan.borrowers.map((b) => `${b.firstName} ${b.lastName}`),
                    ...loan.businesses.map((b) => b.name),
                ].filter(Boolean);
                break;
            case "businessName":
                loanValues = loan.businesses.map((b) => b.name).filter(Boolean);
                break;
            default:
                break;
        }

        const approved = approvedMatchValues[matchableFieldType] ?? [];
        const seen = new Set(
            loanValues.map((v) => (matchableFieldType === "tin" ? normalizeTins(v) : normalizeString(v))),
        );
        const uniqueApproved = approved.filter((a) => !seen.has(a));

        return [...loanValues, ...uniqueApproved];
    }, [matchableFieldType, loan, approvedMatchValues]);

    const isLoanMatch = useMemo<boolean>(() => {
        if (!matchableFieldType || !loan || !value) return false;

        switch (matchableFieldType) {
            case "tin": {
                const tins = [...loan.borrowers.map((b) => b.tin), ...loan.businesses.map((b) => b.tin)]
                    .filter(Boolean)
                    .map(normalizeTins);
                return tins.includes(normalizedValue);
            }
            case "firstName": {
                const firstNames = loan.borrowers.map((b) => normalizeString(b.firstName));
                return firstNames.includes(normalizedValue);
            }
            case "lastName": {
                const lastNames = loan.borrowers.map((b) => normalizeString(b.lastName));
                return lastNames.includes(normalizedValue);
            }
            case "name": {
                const names = loan.borrowers.map(
                    (b) => `${normalizeString(b.firstName)} ${normalizeString(b.lastName)}`,
                );
                const businessNames = loan.businesses.map((b) => normalizeString(b.name));
                return [...names, ...businessNames].includes(normalizedValue);
            }
            case "businessName": {
                const businessNames = loan.businesses.map((b) => normalizeString(b.name));
                return businessNames.includes(normalizedValue);
            }
            default:
                return false;
        }
    }, [matchableFieldType, loan, value, normalizedValue]);

    const isApprovedMatch = useMemo<boolean>(() => {
        if (!matchableFieldType || !value || isLoanMatch) return false;
        const approved = approvedMatchValues[matchableFieldType] ?? [];
        return approved.includes(normalizedValue);
    }, [matchableFieldType, value, isLoanMatch, approvedMatchValues, normalizedValue]);

    const isMatchingField = isLoanMatch || isApprovedMatch;

    // Match status colors
    const matchColor = theme.palette.success.main;
    const noMatchColor = theme.palette.warning.main;

    const isMatchableField = matchableFieldType !== null;

    // Determine border color based on match status, importance, or default
    const getBorderColor = () => {
        if (isMatchableField && value) {
            return isMatchingField ? matchColor : noMatchColor;
        }
        if (isImportant) return importantBorderColor;
        return theme.palette.divider;
    };

    const getBorderWidth = () => {
        if (isMatchableField && value && !isMatchingField) return 2;
        if (isImportant) return 2;
        return 1;
    };

    const textFieldStyles = {
        "& .MuiInputBase-root": {
            backgroundColor:
                isMatchableField && value
                    ? isMatchingField
                        ? alpha(matchColor, 0.08)
                        : alpha(noMatchColor, 0.08)
                    : theme.palette.mode === "dark"
                      ? alpha(theme.palette.background.paper, 0.8)
                      : theme.palette.background.paper,
        },
        "& .MuiInputLabel-root": {
            color: theme.palette.text.secondary,
            ...(isImportant && {
                color: importantBorderColor,
                fontWeight: 600,
            }),
            ...(isMatchableField &&
                value &&
                !isMatchingField && {
                    color: noMatchColor,
                    fontWeight: 600,
                }),
            ...(isMatchableField &&
                value &&
                isMatchingField && {
                    color: matchColor,
                }),
            ...labelStyle,
        },
        "& .MuiInputBase-input": {
            ...inputStyle,
        },
        "& .MuiOutlinedInput-root": {
            "& fieldset": {
                borderColor: getBorderColor(),
                borderWidth: getBorderWidth(),
            },
            "&:hover fieldset": {
                borderColor:
                    isMatchableField && value
                        ? isMatchingField
                            ? matchColor
                            : noMatchColor
                        : isImportant
                          ? importantBorderColor
                          : theme.palette.primary.main,
            },
            "&.Mui-focused fieldset": {
                borderColor:
                    isMatchableField && value
                        ? isMatchingField
                            ? matchColor
                            : noMatchColor
                        : isImportant
                          ? importantBorderColor
                          : theme.palette.primary.main,
                boxShadow:
                    isMatchableField && value
                        ? `0 0 0 3px ${alpha(isMatchingField ? matchColor : noMatchColor, 0.2)}`
                        : isImportant
                          ? `0 0 0 3px ${alpha(importantBorderColor, 0.2)}`
                          : "none",
            },
        },
        ...(width && { width }),
    };

    const handleApprove = useCallback(() => {
        if (matchableFieldType && normalizedValue) {
            dispatch(addApprovedMatchValue(matchableFieldType, normalizedValue));
        }
    }, [dispatch, matchableFieldType, normalizedValue]);

    const handleUnapprove = useCallback(() => {
        if (matchableFieldType && normalizedValue) {
            dispatch(removeApprovedMatchValue(matchableFieldType, normalizedValue));
        }
    }, [dispatch, matchableFieldType, normalizedValue]);

    const getEndAdornment = () => {
        if (!isMatchableField || !value) return undefined;

        if (isLoanMatch) {
            return (
                <InputAdornment position="end">
                    <CheckCircle sx={{ color: matchColor, fontSize: 20 }} />
                </InputAdornment>
            );
        }

        if (isApprovedMatch) {
            return (
                <InputAdornment position="end">
                    <CheckCircle sx={{ color: matchColor, fontSize: 20 }} />
                    <Tooltip title="Remove approval">
                        <IconButton
                            size="small"
                            onClick={handleUnapprove}
                            sx={{ ml: -0.5, p: 0.25 }}
                        >
                            <RemoveCircleOutline sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </InputAdornment>
            );
        }

        return (
            <InputAdornment position="end">
                <Tooltip title="Approve value">
                    <IconButton
                        size="small"
                        onClick={handleApprove}
                        sx={{ p: 0.25 }}
                    >
                        <AddCircleOutline sx={{ color: noMatchColor, fontSize: 20 }} />
                    </IconButton>
                </Tooltip>
            </InputAdornment>
        );
    };

    const wrapWithIndexLabel = (node: React.ReactNode) => {
        if (!indexLabel) return node;
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                <Box
                    component="span"
                    sx={{ fontWeight: 900, whiteSpace: "nowrap", fontSize: "0.95rem" }}
                >
                    {indexLabel}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>{node}</Box>
            </Box>
        );
    };

    if (isMatchableField) {
        return wrapWithIndexLabel(
            <Autocomplete
                freeSolo
                disableClearable
                options={matchableOptions}
                value={value ?? ""}
                sx={{ "& .MuiAutocomplete-input": { minWidth: "10px !important" }, "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": { paddingRight: "6px !important" } }}
                slotProps={{
                    paper: {
                        sx: {
                            bgcolor: theme.palette.mode === "dark" ? "#2a2a2a" : "#fff",
                            border: 1,
                            borderColor: theme.palette.divider,
                            boxShadow: theme.palette.mode === "dark"
                                ? "0 4px 20px rgba(0,0,0,0.5)"
                                : "0 4px 20px rgba(0,0,0,0.15)",
                            "& .MuiAutocomplete-option": {
                                fontSize: "0.85rem",
                                "&:hover": {
                                    bgcolor: theme.palette.mode === "dark"
                                        ? alpha(theme.palette.primary.main, 0.15)
                                        : alpha(theme.palette.primary.main, 0.08),
                                },
                                "&[aria-selected='true']": {
                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                },
                            },
                        },
                    },
                }}
                onInputChange={(_event, newValue, reason) => {
                    if (reason === "input" || reason === "clear") {
                        handleFieldChange(fieldPath, newValue);
                    }
                }}
                onChange={(_event, newValue) => {
                    handleFieldChange(fieldPath, newValue ?? "");
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        fullWidth={!width}
                        sx={textFieldStyles}
                        {...textFieldProps}
                        error={hasError}
                        helperText={errorHelperText || textFieldProps.helperText}
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {getEndAdornment()}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            },
                            htmlInput: {
                                ...params.inputProps,
                                title: value != null && value !== "" ? String(value) : undefined,
                            },
                        }}
                    />
                )}
            />,
        );
    }

    return wrapWithIndexLabel(
        <TextField
            value={value}
            onChange={handleChange}
            size="small"
            fullWidth={!width}
            sx={textFieldStyles}
            {...textFieldProps}
            error={hasError}
            helperText={errorHelperText || textFieldProps.helperText}
            slotProps={{
                input: {
                    endAdornment: getEndAdornment(),
                },
                htmlInput: {
                    title: value != null && value !== "" ? String(value) : undefined,
                },
            }}
        />,
    );
};

export default FormTextField;
